import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../store';

const API = (() => {
  const h = window.location.hostname;
  return process.env.REACT_APP_API_URL || ((h === 'localhost' || h === '127.0.0.1') ? 'http://localhost:5001/api' : `http://${h}:5001/api`);
})();

const fmt = (v: number) => new Intl.NumberFormat('id-ID').format(v);
const fmtDate = (d: string) => new Date(d).toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

interface AddonMutation {
  id: string;
  branch_name: string;
  addon_id: string;
  addon_name: string;
  addon_type: string;
  type: string;
  quantity: number;
  notes?: string;
  user_name?: string;
  created_at: string;
  source: string;
  unit_besar?: string;
  konversi?: number;
  balance_after?: number | null;
  balance_gudang?: number | null;
  balance_chiller?: number | null;
  hpp_at_transaction?: number | null;
}

interface Branch { id: string; name: string; }
interface Addon { id: string; name: string; branch_id: string; addon_type: string; branch_name?: string; gudang_quantity?: number; chiller_quantity?: number; unit_besar?: string; konversi?: number; }

const TYPE_LABEL: Record<string, string> = { in: 'Masuk', out: 'Keluar', return: 'Retur', adjustment: 'Penyesuaian', barang_masuk: 'Masuk Gudang', transfer_in: 'Masuk Chiller', transfer_out: 'Keluar Gudang', out_gudang: 'Keluar Gudang', koreksi_positif: 'Koreksi +', koreksi_negatif: 'Koreksi -' };
const TYPE_COLOR: Record<string, string> = { in: '#16a34a', out: '#dc2626', return: '#2563eb', adjustment: '#7c3aed', barang_masuk: '#1e40af', transfer_in: '#0891b2', transfer_out: '#b45309', out_gudang: '#dc2626', koreksi_positif: '#059669', koreksi_negatif: '#dc2626' };
const TYPE_BG: Record<string, string>    = { in: '#f0fdf4', out: '#fef2f2', return: '#eff6ff', adjustment: '#f5f3ff', barang_masuk: '#dbeafe', transfer_in: '#cffafe', transfer_out: '#fef3c7', out_gudang: '#fef2f2', koreksi_positif: '#d1fae5', koreksi_negatif: '#fee2e2' };

// Which stock column does each mutation type affect?
const TYPE_AFFECTS: Record<string, { icon: string; label: string; dir: '+' | '-' }> = {
  barang_masuk:    { icon: '📦', label: 'Gudang', dir: '+' },
  in:              { icon: '📦', label: 'Gudang', dir: '+' },
  out_gudang:      { icon: '📦', label: 'Gudang', dir: '-' },
  transfer_out:    { icon: '↕️', label: 'Gudang→Chiller', dir: '-' },
  transfer_in:     { icon: '↕️', label: 'Chiller←Gudang', dir: '+' },
  out:             { icon: '🧊', label: 'Chiller', dir: '-' },
  koreksi_positif: { icon: '✅', label: 'Koreksi Chiller', dir: '+' },
  koreksi_negatif: { icon: '⚠️', label: 'Koreksi Chiller', dir: '-' },
};

const ADDON_TYPE_LABEL: Record<string, string> = { konsumsi: 'Konsumsi', peralatan: 'Peralatan', jasa: 'Jasa' };

const s: Record<string, React.CSSProperties> = {
  page: { padding: '24px', maxWidth: 1200, margin: '0 auto' },
  title: { fontSize: 22, fontWeight: 800, color: '#1e293b', marginBottom: 4 },
  sub: { fontSize: 13, color: '#64748b', marginBottom: 24 },
  filterRow: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20, alignItems: 'flex-end' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  filterLabel: { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' },
  select: { padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b', background: '#fff', minWidth: 160, cursor: 'pointer' },
  input: { padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b', background: '#fff' },
  btn: { padding: '8px 18px', borderRadius: 8, background: '#1A73E8', color: '#fff', fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer' },
  btnReset: { padding: '8px 14px', borderRadius: 8, background: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: 13, border: '1.5px solid #e2e8f0', cursor: 'pointer' },
  card: { background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: { padding: '11px 14px', textAlign: 'left' as const, fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: '0.05em', borderBottom: '1px solid #f1f5f9', background: '#f8fafc', whiteSpace: 'nowrap' as const },
  td: { padding: '11px 14px', fontSize: 13, color: '#334155', borderBottom: '1px solid #f8fafc' },
  badge: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 },
  typeBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600 },
  empty: { textAlign: 'center' as const, padding: '48px 0', color: '#94a3b8', fontSize: 14 },
};

export const StockHistoryPage: React.FC = () => {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [mutations, setMutations] = useState<AddonMutation[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab] = useState<'mutations'>('mutations');

  const today = new Date().toISOString().slice(0, 10);
  const threeMonthsAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const [filterBranch, setFilterBranch] = useState(user?.branch_id || '');
  const [filterType, setFilterType] = useState('');
  const [filterAddon, setFilterAddon] = useState('');
  const [startDate, setStartDate] = useState(threeMonthsAgo);
  const [endDate, setEndDate] = useState(today);

  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/branches`, { headers: h })
      .then(r => setBranches(Array.isArray(r.data) ? r.data : (r.data.data || [])))
      .catch(() => {});

    if (['super_admin', 'finance', 'accounting'].includes(user?.role || '')) {
      axios.get(`${API}/pricing/addons/all`, { headers: h })
        .then(r => setAddons(r.data.data || []))
        .catch(() => {});
    } else if (user?.branch_id) {
      axios.get(`${API}/pricing/addons/branch/${user.branch_id}`, { headers: h })
        .then(r => setAddons(r.data.data || []))
        .catch(() => {});
    }
  }, [token, user?.branch_id, user?.role]);

  // Reload addons when branch filter changes (super_admin)
  useEffect(() => {
    if (!['super_admin', 'finance', 'accounting'].includes(user?.role || '')) return;
    const h = { Authorization: `Bearer ${token}` };
    if (filterBranch) {
      axios.get(`${API}/pricing/addons/branch/${filterBranch}`, { headers: h })
        .then(r => setAddons(r.data.data || []))
        .catch(() => {});
    } else {
      axios.get(`${API}/pricing/addons/all`, { headers: h })
        .then(r => setAddons(r.data.data || []))
        .catch(() => {});
    }
    setFilterAddon('');
  }, [filterBranch, token, user?.role]);

  const fetchMutations = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = {};
      if (filterBranch) params.branch_id = filterBranch;
      if (filterType) params.type = filterType;
      if (filterAddon) params.addon_id = filterAddon;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const r = await axios.get(`${API}/stocks/mutations/addon-list`, { headers, params });
      setMutations(r.data.data || []);
    } catch (e: any) {
      setMutations([]);
      const msg = e?.response?.status === 404
        ? 'Endpoint belum tersedia — deploy backend terbaru ke server.'
        : `Gagal memuat data: ${e?.response?.data?.error || e?.message || 'Unknown error'}`;
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [filterBranch, filterType, filterAddon, startDate, endDate, token]);

  useEffect(() => {
    fetchMutations();
  }, [fetchMutations]);

  const handleReset = () => {
    setFilterBranch(user?.branch_id || '');
    setFilterType('');
    setFilterAddon('');
    setStartDate(threeMonthsAgo);
    setEndDate(today);
  };

  const totalIn  = mutations.filter(m => m.type === 'in').reduce((s, m) => s + m.quantity, 0);
  const totalOut = mutations.filter(m => m.type === 'out').reduce((s, m) => s + m.quantity, 0);
  const totalSales = mutations.filter(m => m.source === 'sale').reduce((s, m) => s + m.quantity, 0);

  // Filter addons by type for display (exclude jasa — no stock)
  const filteredAddons = addons.filter(a => a.addon_type !== 'jasa');

  return (
    <div style={s.page}>
      <div style={s.title}>Monitoring Stok</div>


      {activeTab === 'mutations' && (
        <>
          <div style={s.sub}>{mutations.length} entri · {fmt(totalIn)} masuk · {fmt(totalOut + totalSales)} keluar</div>

          {/* Filters */}
      <div style={s.filterRow}>
        {['super_admin', 'finance', 'accounting'].includes(user?.role || '') && (
          <div style={s.filterGroup}>
            <span style={s.filterLabel}>Cabang</span>
            <select style={s.select} value={filterBranch} onChange={e => setFilterBranch(e.target.value)}>
              <option value="">Semua Cabang</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}
        <div style={s.filterGroup}>
          <span style={s.filterLabel}>Tipe Mutasi</span>
          <select style={s.select} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">Semua Tipe</option>
            <option value="barang_masuk">📦 Masuk Gudang</option>
            <option value="out_gudang">📤 Keluar Gudang (koreksi)</option>
            <option value="transfer_out">↕️ Transfer Keluar Gudang</option>
            <option value="transfer_in">↕️ Transfer Masuk Chiller</option>
            <option value="out">🧊 Keluar Chiller / Terjual</option>
            <option value="in">Masuk (Data Lama)</option>
          </select>
        </div>
        <div style={s.filterGroup}>
          <span style={s.filterLabel}>Item / Add-On</span>
          <select style={{ ...s.select, minWidth: 200 }} value={filterAddon} onChange={e => setFilterAddon(e.target.value)}>
            <option value="">Semua Item</option>
            {filteredAddons.map(a => (
              <option key={a.id} value={a.id}>
                {a.name}{['super_admin', 'finance', 'accounting'].includes(user?.role || '') && a.branch_name ? ` · ${a.branch_name}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div style={s.filterGroup}>
          <span style={s.filterLabel}>Dari Tanggal</span>
          <input type="date" style={s.input} value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>
        <div style={s.filterGroup}>
          <span style={s.filterLabel}>Sampai Tanggal</span>
          <input type="date" style={s.input} value={endDate} onChange={e => setEndDate(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <button style={s.btn} onClick={fetchMutations}>Cari</button>
          <button style={s.btnReset} onClick={handleReset}>Reset</button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#dc2626', fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div style={s.card}>
        {loading ? (
          <div style={s.empty}>Memuat...</div>
        ) : mutations.length === 0 ? (
          <div style={s.empty}>Belum ada data stok pada rentang tanggal ini</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Tanggal</th>
                {['super_admin', 'finance', 'accounting'].includes(user?.role || '') && <th style={s.th}>Cabang</th>}
                <th style={s.th}>Item / Add-On</th>
                <th style={s.th}>Tipe Mutasi</th>
                <th style={{ ...s.th, textAlign: 'center' }}>Jumlah</th>
                <th style={{ ...s.th, textAlign: 'center' }}>Sisa Stok</th>
                <th style={s.th}>Keterangan</th>
                <th style={{ ...s.th, textAlign: 'center' }}>HPP (Rp)</th>
                <th style={s.th}>Operator</th>
              </tr>
            </thead>
            <tbody>
              {mutations.map(m => (
                <tr key={m.id} style={{ background: '#fff' }}>
                  <td style={s.td}>{fmtDate(m.created_at)}</td>
                  {['super_admin', 'finance', 'accounting'].includes(user?.role || '') && <td style={s.td}>{m.branch_name || '—'}</td>}
                  <td style={s.td}>
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{m.addon_name || '—'}</div>
                    {m.addon_type && m.addon_type !== 'jasa' && (
                      <span style={{ ...s.typeBadge, background: m.addon_type === 'konsumsi' ? '#fef9c3' : '#e0f2fe', color: m.addon_type === 'konsumsi' ? '#854d0e' : '#0369a1' }}>
                        {ADDON_TYPE_LABEL[m.addon_type] || m.addon_type}
                      </span>
                    )}
                    {(m as any).kode_barang && (
                      <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#475569', marginTop: 3 }}>#{(m as any).kode_barang}</div>
                    )}
                  </td>
                  <td style={s.td}>
                    <span style={{ ...s.badge, color: m.source === 'sale' ? '#dc2626' : m.source === 'bundling' ? '#92400e' : (TYPE_COLOR[m.type] || '#475569'), background: m.source === 'sale' ? '#fef2f2' : m.source === 'bundling' ? '#fef3c7' : (TYPE_BG[m.type] || '#f8fafc') }}>
                      {m.source === 'sale' ? 'Terjual' : m.source === 'bundling' ? 'Bundling' : (TYPE_LABEL[m.type] || m.type)}
                    </span>
                    {(m.source === 'sale' || m.source === 'bundling') && (
                      <div style={{ fontSize: 10, color: '#0891b2', marginTop: 2 }}>dari Chiller</div>
                    )}
                  </td>
                  <td style={{ ...s.td, textAlign: 'center' }}>
                    {(() => {
                      const isIn = m.type === 'in' || m.type === 'barang_masuk' || m.type === 'transfer_in';
                      const color = isIn ? '#16a34a' : '#dc2626';
                      const showKardus = m.unit_besar && m.konversi && m.konversi > 1;
                      const kardusCount = showKardus ? Math.floor(m.quantity / m.konversi!) : 0;
                      const affects = m.source === 'sale' ? { icon: '🛒', label: 'Chiller', dir: '-' as const } : TYPE_AFFECTS[m.type];
                      return (
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 15, color }}>
                            {isIn ? '+' : '-'}{fmt(m.quantity)}
                          </div>
                          {showKardus && (
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                              {kardusCount} {m.unit_besar} @{m.konversi}/pcs
                            </div>
                          )}
                          {affects && (
                            <div style={{ marginTop: 4, display: 'inline-block', padding: '2px 7px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: affects.dir === '+' ? '#dcfce7' : '#fee2e2', color: affects.dir === '+' ? '#15803d' : '#b91c1c' }}>
                              {affects.icon} {affects.dir}{showKardus && affects.label !== 'Chiller' ? `${kardusCount} ${m.unit_besar}` : `${m.quantity}pcs`} {affects.label}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ ...s.td }}>
                    {(m.balance_gudang != null || m.balance_chiller != null) ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {m.balance_gudang != null && (
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>Gudang</div>
                            {m.unit_besar && m.konversi && m.konversi > 1 ? (
                              <>
                                <div style={{ fontWeight: 700, color: '#1e293b' }}>{Math.floor(m.balance_gudang / m.konversi)} {m.unit_besar}</div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>{m.balance_gudang} pcs</div>
                              </>
                            ) : (
                              <div style={{ fontWeight: 700, color: '#1e293b' }}>{fmt(m.balance_gudang)} pcs</div>
                            )}
                          </div>
                        )}
                        {m.balance_chiller != null && (
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#0891b2', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>Chiller</div>
                            <div style={{ fontWeight: 700, color: '#1e293b' }}>{fmt(m.balance_chiller)} pcs</div>
                          </div>
                        )}
                      </div>
                    ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                  </td>
                  <td style={{ ...s.td, color: '#64748b', fontSize: 12 }}>
                    {m.source === 'manual' ? (
                      <div>
                        {m.notes && <div>{m.notes}</div>}
                        <div style={{ color: '#94a3b8', fontSize: 11 }}>Input oleh - {m.user_name || '—'}</div>
                      </div>
                    ) : m.source === 'bundling' ? (
                      <div style={{ color: '#92400e' }}>{m.notes || '—'}</div>
                    ) : m.source === 'sale' ? (
                      <div>
                        <div>{m.notes || '—'}</div>
                        <div style={{ color: '#94a3b8', fontSize: 11 }}>Kasir: {m.user_name || '—'}</div>
                      </div>
                    ) : '—'}
                  </td>
                  <td style={{ ...s.td, textAlign: 'center', fontSize: 12, fontWeight: 600, color: m.hpp_at_transaction ? '#1e293b' : '#cbd5e1' }}>
                    {m.hpp_at_transaction ? `Rp${fmt(m.hpp_at_transaction)}` : '—'}
                  </td>
                  <td style={{ ...s.td, fontSize: 12 }}>{m.user_name || '—'}</td>
                </tr>
              ))}
            </tbody>
            {filterBranch && mutations.length > 0 && (() => {
              const totalMasukGudang = mutations.filter(m => m.type === 'barang_masuk').reduce((s, m) => s + m.quantity, 0);
              const totalMasukLama = mutations.filter(m => m.type === 'in').reduce((s, m) => s + m.quantity, 0);
              const totalTransfer = mutations.filter(m => m.type === 'transfer_in').reduce((s, m) => s + m.quantity, 0);
              const totalKeluarGudang = mutations.filter(m => m.type === 'transfer_out').reduce((s, m) => s + m.quantity, 0);
              const totalTerjual = mutations.filter(m => m.source === 'sale').reduce((s, m) => s + m.quantity, 0);
              const totalBundling = mutations.filter(m => m.source === 'bundling').reduce((s, m) => s + m.quantity, 0);
              const totalKeluar = mutations.filter(m => m.type === 'out' && m.source === 'manual').reduce((s, m) => s + m.quantity, 0);
              const totalKoreksiPos = mutations.filter(m => m.type === 'koreksi_positif').reduce((s, m) => s + m.quantity, 0);
              const totalKoreksiNeg = mutations.filter(m => m.type === 'koreksi_negatif').reduce((s, m) => s + m.quantity, 0);
              const colSpan = ['super_admin', 'finance', 'accounting'].includes(user?.role || '') ? 2 : 1;
              const selectedAddon = filterAddon ? addons.find(a => a.id === filterAddon) : null;
              const showKonversi = selectedAddon?.unit_besar && selectedAddon?.konversi && selectedAddon.konversi > 1;
              return (
                <tfoot>
                  <tr style={{ background: '#f1f5f9', borderTop: '2px solid #cbd5e1' }}>
                    <td colSpan={colSpan} style={{ ...s.td, fontWeight: 800, color: '#1e293b', fontSize: 13 }}>TOTAL</td>
                    <td style={s.td}>—</td>
                    <td style={s.td}>—</td>
                    <td style={{ ...s.td }}>
                      {(() => {
                        const konv = selectedAddon?.konversi && selectedAddon.konversi > 1 ? selectedAddon.konversi : null;
                        const ub = selectedAddon?.unit_besar;
                        const toUB = (n: number) => konv ? ` / ${Math.floor(n / konv)} ${ub}` : '';
                        return (
                          <>
                            {totalMasukGudang > 0 && <div style={{ fontWeight: 700, color: '#1e40af', fontSize: 12 }}>+{fmt(totalMasukGudang)} pcs{toUB(totalMasukGudang)} masuk gudang</div>}
                            {totalMasukLama > 0 && <div style={{ fontWeight: 700, color: '#16a34a', fontSize: 12 }}>+{fmt(totalMasukLama)} pcs{toUB(totalMasukLama)} masuk</div>}
                            {totalKeluarGudang > 0 && <div style={{ fontWeight: 700, color: '#b45309', fontSize: 12 }}>-{fmt(totalKeluarGudang)} pcs{toUB(totalKeluarGudang)} keluar gudang</div>}
                            {totalTransfer > 0 && <div style={{ fontWeight: 700, color: '#0891b2', fontSize: 12 }}>+{fmt(totalTransfer)} pcs{toUB(totalTransfer)} masuk chiller</div>}
                            {totalTerjual > 0 && <div style={{ fontWeight: 700, color: '#dc2626', fontSize: 12 }}>-{fmt(totalTerjual)} pcs{toUB(totalTerjual)} terjual</div>}
                            {totalBundling > 0 && <div style={{ fontWeight: 700, color: '#d97706', fontSize: 12 }}>-{fmt(totalBundling)} pcs{toUB(totalBundling)} bundling</div>}
                            {totalKeluar > 0 && <div style={{ fontWeight: 700, color: '#7c3aed', fontSize: 12 }}>-{fmt(totalKeluar)} pcs{toUB(totalKeluar)} keluar manual</div>}
                            {totalKoreksiPos > 0 && <div style={{ fontWeight: 700, color: '#059669', fontSize: 12 }}>+{fmt(totalKoreksiPos)} pcs koreksi rekonsiliasi</div>}
                            {totalKoreksiNeg > 0 && <div style={{ fontWeight: 700, color: '#dc2626', fontSize: 12 }}>-{fmt(totalKoreksiNeg)} pcs koreksi rekonsiliasi</div>}
                          </>
                        );
                      })()}
                    </td>
                    <td style={{ ...s.td }}>
                      {selectedAddon ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>Gudang Saat Ini</div>
                            {showKonversi ? (
                              <>
                                <div style={{ fontWeight: 700, color: '#1e293b' }}>{Math.floor((selectedAddon.gudang_quantity ?? 0) / selectedAddon.konversi!)} {selectedAddon.unit_besar}</div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>{selectedAddon.gudang_quantity ?? 0} pcs</div>
                              </>
                            ) : (
                              <div style={{ fontWeight: 700, color: '#1e293b' }}>{fmt(selectedAddon.gudang_quantity ?? 0)} pcs</div>
                            )}
                          </div>
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#0891b2', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>Chiller Saat Ini</div>
                            <div style={{ fontWeight: 700, color: '#1e293b' }}>{fmt(selectedAddon.chiller_quantity ?? 0)} pcs</div>
                          </div>
                        </div>
                      ) : <span style={{ color: '#94a3b8', fontSize: 12 }}>Pilih item untuk lihat sisa stok</span>}
                    </td>
                    <td style={s.td}>—</td>
                    <td style={s.td}>—</td>
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        )}
          </div>
        </>
      )}

    </div>
  );
};
